import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import { connect } from "react-redux";
import config from "../../config";
import { authHeader } from "../../_helpers";
import { handleResponse } from "../../_helpers/response";
import { professionalService } from "../../_services";
import { ViewFormModel } from "../ProfessionalForm/ViewForm/ViewFormModel";
import "./imageMap.css";

const PAGE_SIZE = 5;
const LIST_LIMIT = 150;

const searchByName = (name) =>
  fetch(`${config.apiGateway.baseUrl}/image-map/search`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
    },
    body: JSON.stringify({ name }),
  }).then(handleResponse);

const photosByInquiry = (record) =>
  fetch(`${config.apiGateway.baseUrl}/image-map/photos`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
    },
    body: JSON.stringify({
      id: record.id,
      name: record.english || record.name,
    }),
  }).then(handleResponse);

const uploadOneImage = (key, masterId, index) =>
  fetch(`${config.apiGateway.baseUrl}/image-map/upload`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
    },
    body: JSON.stringify({ key, masterId, index }),
  }).then(handleResponse);

const commitMapping = (body) =>
  fetch(`${config.apiGateway.baseUrl}/image-map/commit`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
    },
    body: JSON.stringify(body),
  }).then(handleResponse);

const loadCdwaRows = () =>
  fetch(`${config.apiGateway.baseUrl}/image-provision/cdwa`, {
    headers: authHeader(),
  }).then(handleResponse);

const cellText = (value) => (value == null ? "" : String(value).trim());

const isMapped = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const isMappedImage = (record, mappings) =>
  Boolean((mappings && mappings[record.id]) || (record && record.isMap));

const photoName = (photo) => {
  if (typeof photo === "string") return photo.trim();
  const name = `${(photo && photo.name) || ""}`.trim();
  if (name) return name;
  const key = `${(photo && photo.key) || ""}`.trim();
  return key ? key.split("/").pop() : "";
};

const selectedNames = (value) => {
  let photo = value;
  if (typeof photo === "string") {
    try {
      photo = JSON.parse(photo);
    } catch (error) {
      photo = [];
    }
  }
  const list = Array.isArray(photo)
    ? photo
    : photo && Array.isArray(photo.names)
    ? photo.names
    : photo && Array.isArray(photo.photos)
    ? photo.photos
    : [];
  const names = [];
  list.forEach((item) => {
    const name = photoName(item);
    if (name && !names.includes(name)) names.push(name);
  });
  const target = photo && !Array.isArray(photo) ? photo.target || null : null;
  return { names, target };
};

const selectionFromSaved = (saved, images) => {
  const names = selectedNames(saved && saved.photos).names;
  if (!names.length || !images.length) return null;
  const matched = [];
  names.forEach((name) => {
    const image = images.find((item) => item.name === name || item.key === name);
    if (image && !matched.some((item) => item.key === image.key)) matched.push(image);
  });
  if (!matched.length) return null;
  const primaryImage =
    matched.find((image) => image.name === names[0] || image.key === names[0]) ||
    matched[0];
  return {
    keys: matched.map((image) => image.key),
    primary: primaryImage.key,
  };
};

const recordFromCdwa = (item) => {
  let extra = item && item.rows;
  if (typeof extra === "string") {
    try {
      extra = JSON.parse(extra);
    } catch (error) {
      extra = {};
    }
  }
  if (!extra || typeof extra !== "object") extra = {};
  const id = cellText(item && item.enquiry_id) || cellText(extra["Inquiry ID"]);
  const mapped = isMapped(item && item.is_map);
  const stored = selectedNames(item && item.photo);
  const masterId = Number(item && (item.masterid != null ? item.masterid : item.masterId)) || 0;
  const mappedTarget = masterId
    ? { ...(stored.target || {}), cmis_id: String(masterId) }
    : stored.target;
  return {
    id,
    name: extra["Full Name (Devnagari)"],
    english: extra["Full Name (English)"],
    age: extra.Age,
    mobile: extra["Mobile no"],
    aadhaar: extra["Aadhaar no"],
    dob: extra.DOB,
    address: extra["Permanent Address"] || extra["Temporary Address"],
    ps: extra["Jurisdiction PS"] || extra["Created By"],
    isMap: mapped,
    imageStatus: mapped ? "is_map" : "",
    photoCount: Number(item && item.photo_count) || stored.names.length,
    selectedPhotos: stored.names,
    masterId,
    mappedTarget,
    extra,
  };
};

const display = (value) => (value ? value : "उपलब्ध नाही");

const imageStatusText = (available, loading) => {
  if (loading) return "तपासत आहे…";
  if (available === true) return "उपलब्ध";
  if (available === false) return "उपलब्ध नाही";
  return "माहित नाही";
};

const ProfileCard = ({
  record,
  source,
  inquiryId,
  imageAvailable,
  imageLoading,
  onNameClick,
}) => {
  const fields = [
    ["Name", record.name || record.english, true],
    ["Address", record.address, true],
    ["Age", record.age, false],
    ["Mobile", record.mobile, false],
    ["Aadhaar", record.aadhaar, false],
    ["DOB", record.dob, false],
    ["Image", imageStatusText(imageAvailable, imageLoading), false],
  ];

  return (
    <>
      <span className="tag id-tag">
        {source ? "Inquiry ID: " : "CMIS ID: "}
        <b>{source ? inquiryId : record.cmis_id}</b>
      </span>
      {record.manual ? (
        <div className="hint suggestion-note">
          हा ID हाताने दिला आहे. सर्व्हरवर पडताळला नाही.
        </div>
      ) : null}
      <div className="details">
        {fields.map(([label, value, wide]) => (
          <div key={label} className={wide ? "wide" : ""}>
            <small>{label}</small>
            {label === "Name" ? (
              <button type="button" className="name-link" onClick={onNameClick}>
                <strong>{display(value)}</strong>
              </button>
            ) : (
              <span>{label === "Image" ? value : display(value)}</span>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

const ImageMap = ({ professionals, user }) => {
  const [records, setRecords] = useState([]);
  const [excelError, setExcelError] = useState("");
  const [loadingExcel, setLoadingExcel] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("pending");
  const [currentId, setCurrentId] = useState("");
  const [centerQuery, setCenterQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searchSummary, setSearchSummary] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [folder, setFolder] = useState("");
  const [photoNote, setPhotoNote] = useState("");
  const [photoPage, setPhotoPage] = useState(0);
  const [primaryKey, setPrimaryKey] = useState("");
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [photoCounts, setPhotoCounts] = useState({});
  const [verified, setVerified] = useState(false);
  const [mappings, setMappings] = useState({});
  const [status, setStatus] = useState("नाव व फोटो निवडून Mapping करा.");
  const [bucket, setBucket] = useState("cdwa");
  const [prefix, setPrefix] = useState("");
  const [destination, setDestination] = useState("pcImageupload");
  const [manualId, setManualId] = useState("");
  const [toast, setToast] = useState("");
  const [zoom, setZoom] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileId, setProfileId] = useState("");
  const [profileView, setProfileView] = useState(null);
  const mappingsRef = useRef({});
  const currentIdRef = useRef("");
  const selectToken = useRef(0);
  const searchToken = useRef(0);
  const profileRequest = useRef(0);
  const toastTimer = useRef(null);

  const showToast = (text) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 3500);
  };

  const current = records.find((record) => record.id === currentId) || null;

  const openProfile = (cmisId) => {
    const id = cmisId == null ? "" : String(cmisId).trim();
    if (!id || id === "0") {
      showToast("या नावासाठी CMIS प्रोफाइल उपलब्ध नाही.");
      return;
    }
    const token = ++profileRequest.current;
    setProfileView(null);
    setProfileId(id);
    setProfileOpen(true);
    professionalService.getCriminalsById(id).then(
      (criminalViewList) => {
        if (profileRequest.current !== token) return;
        setProfileView(criminalViewList);
      },
      () => {
        if (profileRequest.current !== token) return;
        showToast("प्रोफाइल लोड करता आली नाही.");
      }
    );
  };

  const profileStates = useMemo(
    () => ({
      ...(professionals || {}),
      criminalViewList: profileView || {},
    }),
    [professionals, profileView]
  );

  const closeProfile = () => setProfileOpen(false);

  const visible = useMemo(() => {
    const needle = query.toLowerCase().trim();
    return records.filter((record) => {
      const haystack = `${record.id} ${record.name} ${record.english} ${record.ps}`.toLowerCase();
      if (needle && !haystack.includes(needle)) return false;
      if (filter === "pending") return !isMappedImage(record, mappings);
      if (filter === "saved") return isMappedImage(record, mappings);
      if (filter === "photos") return photoCounts[record.id] > 0;
      return true;
    });
  }, [records, query, filter, mappings, photoCounts]);

  const pageCount = Math.max(1, Math.ceil(photos.length / PAGE_SIZE));
  const pagePhotos = photos.slice(
    photoPage * PAGE_SIZE,
    photoPage * PAGE_SIZE + PAGE_SIZE
  );
  const primary = photos.find((photo) => photo.key === primaryKey);
  const canSave = Boolean(
    chosen &&
      photos.length &&
      primary &&
      selectedKeys.includes(primary.key) &&
      destination.trim() &&
      bucket.trim() &&
      verified
  );

  const runSearch = (value, { clearChosen }) => {
    const name = value.trim();
    if (name.length < 2) {
      showToast("शोधण्यासाठी नाव किंवा CMIS ID द्या.");
      return;
    }
    if (clearChosen) {
      setChosen(null);
      setVerified(false);
    }
    const token = ++searchToken.current;
    setSearched(true);
    setSearchLoading(true);
    setSearchSummary(`शोध: ${name}`);
    searchByName(name)
      .then((data) => {
        if (token !== searchToken.current) return;
        const results = data.results || [];
        setCandidates(results);
        if (!clearChosen) {
          const savedNow = mappingsRef.current[currentIdRef.current];
          const cmisId =
            (savedNow && savedNow.cmis_id) ||
            (savedNow && savedNow.target && savedNow.target.cmis_id);
          const match = results.find((person) => String(person.id) === String(cmisId));
          if (match) {
            setChosen({
              cmis_id: String(match.id),
              name: match.name,
              address: match.address,
              age: match.age,
              mobile: match.mobile,
              aadhaar: match.aadhaar,
              dob: "",
              gunaCount: match.gunaCount,
              hasImage: Boolean(match.hasImage),
            });
          }
        }
        setSearchSummary(
          `शोध: ${name} · पहिले ${results.length} पर्याय`
        );
        if (data.s3Error) showToast(data.s3Error);
      })
      .catch((error) => {
        if (token !== searchToken.current) return;
        setCandidates([]);
        showToast(error || "Search failed");
      })
      .finally(() => {
        if (token === searchToken.current) setSearchLoading(false);
      });
  };

  const selectRecord = (record) => {
    const token = ++selectToken.current;
    const saved = mappingsRef.current[record.id];
    currentIdRef.current = record.id;
    setCurrentId(record.id);
    setCenterQuery(record.name || record.english || "");
    setVerified(false);
    setPhotoPage(0);
    setManualId("");
    setChosen(saved ? saved.target : null);
    setStatus(
      saved
        ? "✓ या नोंदीचे मॅपिंग सेव्ह आहे."
        : "नाव व फोटो निवडून Mapping करा."
    );
    setPhotos([]);
    setFolder("");
    setPhotoLoading(true);
    setPhotoNote("फोटो लोड होत आहेत…");
    const restored = selectionFromSaved(saved, []);
    setSelectedKeys(restored ? restored.keys : []);
    setPrimaryKey(restored ? restored.primary : "");

    const searchName = record.name || record.english || "";
    if (searchName.trim().length >= 2) {
      runSearch(searchName, { clearChosen: false });
    }

    photosByInquiry(record)
      .then((data) => {
        if (token !== selectToken.current) return;
        const images = data.images || [];
        const savedNow = mappingsRef.current[record.id];
        const restoredNow = selectionFromSaved(savedNow, images);
        setPhotos(images);
        setFolder(data.folder || "");
        setPhotoLoading(false);
        setPhotoNote(
          data.s3Error ||
            (images.length
              ? `${images.length} फोटो · cdwa bucket`
              : "या Inquiry ID साठी cdwa bucket मध्ये फोटो सापडले नाहीत.")
        );
        if (restoredNow) {
          setSelectedKeys(restoredNow.keys);
          setPrimaryKey(restoredNow.primary);
          if (savedNow && savedNow.target) setChosen(savedNow.target);
        } else {
          setPrimaryKey(images[0] ? images[0].key : "");
          setSelectedKeys([]);
        }
      })
      .catch(() => {
        if (token !== selectToken.current) return;
        setPhotoLoading(false);
        setPhotoNote("या Inquiry ID साठी cdwa bucket मधील फोटो वाचता आले नाहीत.");
      });
  };

  useEffect(() => {
    let active = true;
    loadCdwaRows()
      .then((data) => {
        if (!active) return;
        const loaded = (data.rows || [])
          .map(recordFromCdwa)
          .filter((record) => record.id);
        const counts = {};
        const nextMappings = {};
        loaded.forEach((record) => {
          counts[record.id] = record.photoCount || 0;
          if (record.selectedPhotos.length || record.masterId) {
            nextMappings[record.id] = {
              inquiry_id: record.id,
              source_name: record.name || record.english,
              cmis_id: record.masterId
                ? String(record.masterId)
                : record.mappedTarget && record.mappedTarget.cmis_id,
              target: record.mappedTarget,
              photos: record.selectedPhotos,
              reviewed: Boolean(record.isMap),
            };
          }
        });
        mappingsRef.current = nextMappings;
        setMappings(nextMappings);
        setPhotoCounts(counts);
        setRecords(loaded);
        const firstPending = loaded.find(
          (record) => !isMappedImage(record, nextMappings)
        );
        if (firstPending) selectRecord(firstPending);
        else if (loaded[0]) selectRecord(loaded[0]);
      })
      .catch((error) => {
        if (active) setExcelError(error || "CDWA data load failed");
      })
      .finally(() => {
        if (active) setLoadingExcel(false);
      });
    return () => {
      active = false;
    };
    // Load saved CDWA rows once. selectRecord is created per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePhoto = (key, checked) => {
    const next = checked
      ? [...new Set([...selectedKeys, key])]
      : selectedKeys.filter((item) => item !== key);
    setSelectedKeys(next);
    if (!next.includes(primaryKey)) {
      const fallback = photos.find((photo) => next.includes(photo.key));
      setPrimaryKey(fallback ? fallback.key : "");
    }
  };

  const saveMapping = async () => {
    if (!canSave || !current || !primary || saving) return null;
    const inquiry = current;
    const target = chosen;
    const selected = photos.filter((photo) =>
      selectedKeys.includes(photo.key)
    );
    const ordered = [
      primary,
      ...selected.filter((photo) => photo.key !== primary.key),
    ];
    setSaving(true);
    setStatus("निवडलेले फोटो एक एक करून अपलोड होत आहेत…");
    try {
      const imageNames = [];
      for (let index = 0; index < ordered.length; index += 1) {
        setStatus(`फोटो ${index + 1} / ${ordered.length} अपलोड होत आहे…`);
        const uploaded = await uploadOneImage(
          ordered[index].key,
          target.cmis_id,
          index
        );
        if (!uploaded || !uploaded.imageName) {
          throw new Error("Image name was not returned");
        }
        imageNames.push(uploaded.imageName);
      }
      const mappedNames = ordered.map((photo) => photo.name).filter(Boolean);
      const saved = await commitMapping({
        masterId: target.cmis_id,
        enquiryId: inquiry.id,
        imageNames,
        photos: mappedNames,
      });
      const savedNames = Array.isArray(saved && saved.photo)
        ? saved.photo
        : mappedNames;
      const payload = {
        inquiry_id: inquiry.id,
        source_name: inquiry.name || inquiry.english,
        cmis_id: String(saved.masterId || target.cmis_id),
        target: { ...target, cmis_id: String(saved.masterId || target.cmis_id) },
        bucket: bucket.trim(),
        destination_bucket: destination.trim() || "pcimageupload",
        photos: savedNames,
        imageNames: saved.imageNames || [],
        accusedImage: saved.accusedImage || "",
        reviewed: true,
        reviewed_at: new Date().toISOString(),
        cmis_updates_applied: true,
      };
      const next = { ...mappingsRef.current, [inquiry.id]: payload };
      mappingsRef.current = next;
      setMappings(next);
      setPhotoCounts((previous) => ({
        ...previous,
        [inquiry.id]: savedNames.length,
      }));
      setSelectedKeys(ordered.map((photo) => photo.key));
      setPrimaryKey(primary.key);
      setRecords((previous) =>
        previous.map((record) =>
          record.id === inquiry.id
            ? {
                ...record,
                isMap: true,
                imageStatus: "is_map",
                photoCount: savedNames.length,
                selectedPhotos: savedNames,
                masterId: Number(saved.masterId) || Number(target.cmis_id) || 0,
                mappedTarget: {
                  ...target,
                  cmis_id: String(saved.masterId || target.cmis_id),
                },
              }
            : record
        )
      );
      setStatus("✓ फोटो अपलोड झाले.");
      showToast("फोटो अपलोड झाले. Mapped image status अपडेट झाला.");
      const top = document.querySelector(".photo-map .top");
      if (top && typeof top.scrollIntoView === "function") {
        top.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return payload;
    } catch (error) {
      setStatus("फोटो अपलोड अयशस्वी.");
      showToast(error || "Image upload failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const skip = () => {
    const list = visible.length ? visible : records;
    if (!list.length) return;
    const index = list.findIndex((record) => record.id === currentId);
    selectRecord(list[(index + 1) % list.length]);
  };

  const photoSlots = Array.from({ length: PAGE_SIZE }, (_, slot) => {
    const photo = pagePhotos[slot];
    const index = photoPage * PAGE_SIZE + slot;
    if (!photo) {
      return (
        <div className="photo-placeholder" key={`empty-${index}`}>
          फोटो {index + 1}
          <br />
          {photos.length ? "फोटो उपलब्ध नाही" : "CDWA फोटो नाही"}
        </div>
      );
    }
    const picked = selectedKeys.includes(photo.key);
    const isPrimary = photo.key === primaryKey;
    return (
      <div
        className={`photo${picked || isPrimary ? " selected" : ""}`}
        key={photo.key}
      >
        <img
          src={photo.url}
          alt={photo.name}
          onClick={() => setZoom(photo.url)}
        />
        <label className="checks">
          <input
            type="checkbox"
            checked={picked}
            onChange={(event) => togglePhoto(photo.key, event.target.checked)}
          />
          फोटो निवडा
        </label>
        <button
          type="button"
          onClick={() => {
            setPrimaryKey(photo.key);
            setSelectedKeys((previous) =>
              previous.includes(photo.key) ? previous : [...previous, photo.key]
            );
          }}
        >
          {isPrimary ? "✓ Primary · " : ""}
          {photo.name}
        </button>
      </div>
    );
  });

  const sourcePath = folder
    ? `s3://${bucket.trim()}/${folder.replace(/\/?$/, "/")}`
    : `s3://${bucket.trim()}/${prefix.trim().replace(/^\/+|\/+$/g, "")}${
        prefix.trim() ? "/" : ""
      }${current ? current.id : ""}_<name>/`;

  return (
    <div className="photo-map">
      
      <main>
        <div className="top">
          <div>
            <h1>नाव सुचवा. तपासा. मॅप करा.</h1>
            <span className="muted">
              १ · CDWA नाव निवडा &nbsp; → &nbsp; २ · CMIS नाव निवडा &nbsp; →
              &nbsp; ३ · फोटो मॅप करा
            </span>
          </div>
        </div>

        {/*<details className="panel setup-details">
          <summary>
            डेटा / फोटो स्रोत{" "}
            <span className="muted">· PC_Cdwa_Inquiry · cdwa bucket</span>
          </summary>
          <div className="setup">
            <div>
              <label className="caption">Source bucket label</label>
              <input value={bucket} onChange={(event) => setBucket(event.target.value)} />
            </div>
            <div>
              <label className="caption">Source prefix</label>
              <input value={prefix} onChange={(event) => setPrefix(event.target.value)} />
            </div>
            <div>
              <label className="caption">Destination label</label>
              <input
                value={destination}
                onChange={(event) => setDestination(event.target.value)}
              />
            </div>
          </div>
          <p className="hint">
            CDWA नोंदी PC_Cdwa_Inquiry मधून लोड होतात. इमेज मॅप केल्यावर
            is_map अपडेट होतो. Excel डाउनलोड होत नाही.
          </p>
        </details>
        */}

        <div className="compact-stats">
          <span>
            CDWA <b>{records.length.toLocaleString("en-IN")}</b>
          </span>
         
  
          <span className="tag">
            Mapped image{" "}
            <b>
              {records.filter((record) => isMappedImage(record, mappings)).length}
            </b>
          </span>
        </div>

        <div className="pickers">
          <section className="panel">
            <div className="panelhead">
              <h2>① CDWA नावे</h2>
              <span className="tag">{visible.length}</span>
            </div>
            <div className="left-search">
              <input
                value={query}
                placeholder="नाव / Inquiry ID शोधा"
                onChange={(event) => setQuery(event.target.value)}
              />
              <select
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
              >
                <option value="all">सर्व</option>
                <option value="pending">बाकी</option>
                <option value="saved">Mapped image</option>
                <option value="photos">फोटो असलेले</option>
              </select>
            </div>
            <div className="list">
              {loadingExcel ? (
                <div className="empty">CDWA नोंदी लोड होत आहेत…</div>
              ) : excelError ? (
                <div className="empty">{excelError}</div>
              ) : (
                visible.slice(0, LIST_LIMIT).map((record) => {
                  const saved = isMappedImage(record, mappings);
                  const count =
                    photoCounts[record.id] ||
                    (record.selectedPhotos ? record.selectedPhotos.length : 0);
                  return (
                    <div
                      key={record.id}
                      className={`row${record.id === currentId ? " active" : ""}`}
                      onClick={() => selectRecord(record)}
                    >
                      <small>
                        <span className="id-inline">Inquiry ID: {record.id}</span>
                        {" · "}
                        {record.ps || "—"}
                        {record.masterId ? (
                          <>
                            {" · "}
                            <span className="id-inline">CMIS ID: {record.masterId}</span>
                          </>
                        ) : (
                          ""
                        )}
                        {record.isMap ? " · is_map" : ""}
                      </small>
                      <strong>{record.name || record.english}</strong>
                      <span className={`pill${saved ? " saved" : ""}`}>
                        {record.isMap
                          ? count > 0
                            ? `is_map · ${count}`
                            : "is_map"
                          : count > 0
                          ? `${count} फोटो`
                          : "S3"}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
            <div className="hint list-foot">
              {loadingExcel
                ? "CDWA नोंदी लोड होत आहेत…"
                : visible.length > LIST_LIMIT
                ? "पहिल्या १५० नोंदी दाखवल्या आहेत. शोध वापरून बाकी नोंदी पाहा."
                : visible.length
                ? "नोंद निवडून फोटो व प्रोफाइल तपासा."
                : "या शोधात नोंद मिळाली नाही."}
            </div>
          </section>

          <section className="panel">
            <div className="panelhead">
              <h2>② CMIS · सुचवलेली १० नावे</h2>

            </div>
            <div className="match-search">
              <input
                value={centerQuery}
                placeholder="नाव"
                onChange={(event) => setCenterQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    runSearch(centerQuery, { clearChosen: true });
                  }
                }}
              />
              <button
                type="button"
                className="primary"
                onClick={() => runSearch(centerQuery, { clearChosen: true })}
              >
                Search
              </button>
            </div>
            
            <div className="results-scroll">
              {!searched ? (
                <div className="empty">
                  ⌕
                  <br />
                  आरोपीची माहिती व फोटो पहा.
                  <br />
                  <b>Search</b> क्लिक करा.
                </div>
              ) : candidates.length === 0 && !searchLoading ? (
                <div className="empty">या शोधात CMIS नोंद मिळाली नाही.</div>
              ) : (
                <>
                  <div className="hint suggestion-note">
                    नाव शोधा
                  </div>
                  <div className="match-columns">
                    <span>#</span>
                    <span>CMIS नाव / पत्ता</span>
                    <span>वय</span>
                    <span>निवड</span>
                  </div>
                  {candidates.map((person, index) => {
                    const picked = chosen && String(chosen.cmis_id) === String(person.id);
                    return (
                      <div
                        className={`match-row${picked ? " chosen" : ""}`}
                        key={person.id}
                      >
                        <span className="rank">{index + 1}</span>
                        <div>
                          <button
                            type="button"
                            className="name-link"
                            onClick={() => openProfile(person.id)}
                          >
                            <strong>{person.name}</strong>
                          </button>
                          <small>
                            <span className="id-inline">CMIS ID: {person.id}</span>
                            {" · "}
                            {person.address || "पत्ता उपलब्ध नाही"}
                            {person.gunaCount ? ` · गुन्हे ${person.gunaCount}` : ""}
                            {person.hasImage ? " · इमेज उपलब्ध" : " · इमेज उपलब्ध नाही"}
                          </small>
                        </div>
                        <span>{person.age || "—"}</span>
                        <button
                          type="button"
                          className="choose"
                          onClick={() => {
                            setChosen({
                              cmis_id: String(person.id),
                              name: person.name,
                              address: person.address,
                              age: person.age,
                              mobile: person.mobile,
                              aadhaar: person.aadhaar,
                              dob: "",
                              gunaCount: person.gunaCount,
                              hasImage: Boolean(person.hasImage),
                            });
                            setVerified(false);
                          }}
                        >
                          {picked ? "✓" : "Select"}
                        </button>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </section>
        </div>

        <div className="review-heading">
          <h2>③ निवडलेल्या नावांची तुलना</h2>
          <span className="hint">दोन्ही बाजू तपासा आणि खाली फोटो निवडा.</span>
        </div>

        <div className="comparison">
          <section className="panel">
            <div className="panelhead">
              <h2>CDWA</h2>
              <span className="tag">Excel मधील माहिती</span>
            </div>
            <div className="body">
              {current ? (
                <ProfileCard
                  record={current}
                  source
                  inquiryId={current.id}
                  imageAvailable={photos.length > 0}
                  imageLoading={photoLoading}
                  onNameClick={() => openProfile(current.masterId)}
                />
              ) : (
                <div className="empty">CDWA नाव निवडा.</div>
              )}
            </div>
            <details className="extra-block">
              <summary>Excel मधील इतर माहिती</summary>
              <div className="extra-info">
                <table className="compare">
                  <tbody>
                    {Object.entries(current?.extra || {}).map(([key, value]) => (
                      <tr key={key}>
                        <td>{key}</td>
                        <td>{value || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </section>
          <section className="panel">
            <div className="panelhead cmis-head">
              <h2>CMIS</h2>
              <span className="tag">
                {chosen ? "Target · Selected" : "नाव निवडा"}
              </span>
            </div>
            <div className="body">
              {chosen ? (
                <ProfileCard
                  record={chosen}
                  imageAvailable={chosen.hasImage}
                  onNameClick={() => openProfile(chosen.cmis_id)}
                />
              ) : (
                <div className="empty">
                  उजवीकडील Top 10 मधून <b>Select</b> करा.
                  <br />
                  निवडलेल्या CMIS प्रोफाइलची माहिती येथे दिसेल.
                </div>
              )}
            </div>
          </section>
        </div>

        <section className="panel photo-panel">
          <div className="panelhead">
            <h2>④ मॅप करायचे फोटो निवडा</h2>
            <span className="hint">फोटोवर क्लिक करून मोठा पहा.</span>
          </div>
          <div className="body">
            <div className="hint">{photoNote}</div>
            <div className="photos">{photoSlots}</div>
            {photos.length > PAGE_SIZE ? (
              <div className="photo-pages">
                <button
                  type="button"
                  disabled={photoPage === 0}
                  onClick={() => setPhotoPage((page) => page - 1)}
                >
                  ← मागील ५
                </button>
                <span className="hint">
                  {photoPage + 1} / {pageCount}
                </span>
                <button
                  type="button"
                  disabled={(photoPage + 1) * PAGE_SIZE >= photos.length}
                  onClick={() => setPhotoPage((page) => page + 1)}
                >
                  पुढील ५ →
                </button>
              </div>
            ) : null}
           {/*<details>
              <summary>Source folder</summary>
              <div className="path">{sourcePath}</div>
            </details>*/}
          </div>
          <div className="action-bar">
            <div>
              <label className="checks">
                <input
                  type="checkbox"
                  checked={verified}
                  onChange={(event) => setVerified(event.target.checked)}
                />
                <span>दोन्ही प्रोफाइल व फोटो तपासले; हीच व्यक्ती आहे.</span>
              </label>
              <div className="status">{status}</div>
            </div>
            <div className="action-buttons">
              {current && mappings[current.id] ? (
                <button
                  type="button"
                  onClick={() => {
                    const next = { ...mappingsRef.current };
                    delete next[current.id];
                    mappingsRef.current = next;
                    setMappings(next);
                    setStatus("नाव व फोटो निवडून Mapping करा.");
                    showToast("मॅपिंग काढले.");
                  }}
                >
                  मॅपिंग काढा
                </button>
              ) : null}
              {/*<button type="button" onClick={skip} disabled={saving}>
                Skip →
              </button>*/}
              <button
                type="button"
                className="primary"
                disabled={!canSave || saving}
                onClick={saveMapping}
              >
                {saving ? "अपलोड होत आहे…" : "✓ Mapping"}
              </button>
             
            </div>
          </div>
        </section>

       
       
      </main>
      <Modal
        title="आरोपी प्रोफाइल"
        visible={profileOpen}
        onOk={closeProfile}
        onCancel={closeProfile}
        width="95%"
        destroyOnClose
      >
        <ViewFormModel
          key={`${(user && user.id) || "user"}-${profileId}`}
          dataStates={profileStates}
        />
      </Modal>
      {toast ? <div className="toast">{toast}</div> : null}
      {zoom ? (
        <div className="modal" onClick={() => setZoom("")}>
          <button type="button" onClick={() => setZoom("")}>
            बंद ✕
          </button>
          <img src={zoom} alt="निवडलेला फोटो" />
        </div>
      ) : null}
    </div>
  );
};

function mapStateToProps(state) {
  const { authentication } = state;
  return {
    professionals: state.professionals,
    user: authentication && authentication.user,
  };
}

const connectedImageMap = connect(mapStateToProps)(ImageMap);
export { connectedImageMap as ImageMap };
